import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { ExternalLink, Globe } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const WHO_URL = "https://www.who.int/groups/commission-on-social-connection";
const AIHW_URL = "https://www.aihw.gov.au/reports/australias-welfare/social-isolation-and-loneliness";

// WHO Commission on Social Connection (2025) — estimated loneliness by age group
const whoAgeData = [
  { age: "13–17", pct: 20.9 },
  { age: "18–29", pct: 17.4 },
  { age: "30–59", pct: 15.1 },
  { age: "60+", pct: 11.8 },
];

// AIHW analysis of HILDA Survey (waves 1–24) — social isolation, 2019 / 2021 / 2024 only
const aihwData = [
  { year: "2019", young: 12, all: 13 },
  { year: "2021", young: 22, all: 16 },
  { year: "2024", young: 17, all: 15 },
];

const auCards = [
  { value: "15%", body: "of Australians experienced loneliness in 2024" },
  { value: "15%", body: "of Australians aged 15+ experienced social isolation in 2024" },
  { value: "17%", body: "loneliness among Australians aged 35–44 in 2024 — the highest rate reported across age groups" },
];

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid #E3EEF2",
  boxShadow: "0 8px 24px rgba(6,27,68,0.10)",
  fontSize: 12,
  color: "#061B44",
};

const SourceLine = ({ label, url, linkText, testid }) => (
  <p className="mt-4 text-xs text-bodygrey" data-testid={testid}>
    Source: {label} ·{" "}
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-teal hover:underline">
      {linkText} <ExternalLink size={12} />
    </a>
  </p>
);

// "The Data Behind Human Connection" — WHO + AIHW visuals. Every figure is source-labelled;
// charts show only verified data points (no interpolation).
export const ConnectionData = () => (
  <section className="border-t border-border bg-white" data-testid="benefits-data-section">
    <div className="container-x section-space">
      <Reveal className="max-w-3xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-teal">The evidence in numbers</span>
        <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">The Data Behind Human Connection</h2>
        <p className="mt-4 text-bodygrey">
          Human connection isn't only something we feel. Researchers around the world are studying how loneliness,
          social isolation, uncertainty and social relationships can influence our wellbeing and behaviour.
        </p>
      </Reveal>

      {/* WHO global callout */}
      <Reveal className="mt-10">
        <div className="flex flex-col items-start gap-5 rounded-3xl border border-teal/20 bg-teal/5 p-8 md:flex-row md:items-center md:gap-8" data-testid="who-callout">
          <span className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-white text-teal shadow-soft"><Globe size={26} /></span>
          <div>
            <p className="font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">1 in 6</p>
            <p className="mt-1 font-semibold text-navy">people worldwide experience loneliness.</p>
            <p className="mt-2 max-w-2xl text-sm text-bodygrey">
              The World Health Organization's Commission on Social Connection identified loneliness and social
              isolation as significant global health and wellbeing challenges.
            </p>
            <SourceLine label="World Health Organization — Commission on Social Connection (2025)" url={WHO_URL} linkText="View WHO research →" testid="who-callout-source" />
          </div>
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Graph 1 — WHO loneliness by age */}
        <Reveal>
          <div className="h-full rounded-3xl border border-border bg-lightbg p-6 shadow-soft md:p-8" data-testid="who-age-chart">
            <h3 className="font-heading text-lg font-semibold text-navy">Loneliness affects every generation</h3>
            <p className="mt-1 text-xs text-bodygrey">Estimated loneliness by age group (%)</p>
            <div className="mt-5 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={whoAgeData} margin={{ top: 20, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E3EEF2" vertical={false} />
                  <XAxis dataKey="age" tick={{ fontSize: 12, fill: "#061B44" }} axisLine={{ stroke: "#E3EEF2" }} tickLine={false} />
                  <YAxis domain={[0, 25]} tick={{ fontSize: 11, fill: "#5A6B85" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(24,184,180,0.08)" }} formatter={(v) => [`${v}%`, "Estimated loneliness"]} />
                  <Bar dataKey="pct" radius={[8, 8, 0, 0]} maxBarSize={64} label={{ position: "top", fontSize: 12, fill: "#061B44", fontWeight: 600, formatter: (v) => `${v}%` }}>
                    {whoAgeData.map((d) => (
                      <Cell key={d.age} fill={d.age === "13–17" ? "#FF6A30" : "#18B8B4"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <SourceLine label="World Health Organization — Commission on Social Connection (2025)" url={WHO_URL} linkText="View research →" testid="who-age-source" />
            <p className="mt-2 text-sm text-bodygrey">
              WHO estimates that around 1 in 6 people globally experience loneliness, with some of the highest rates
              seen among younger people.
            </p>
          </div>
        </Reveal>

        {/* Graph 2 — AIHW social isolation among young Australians */}
        <Reveal delay={0.08}>
          <div className="h-full rounded-3xl border border-border bg-lightbg p-6 shadow-soft md:p-8" data-testid="aihw-isolation-chart">
            <h3 className="font-heading text-lg font-semibold text-navy">Social isolation among young Australians</h3>
            <p className="mt-1 text-xs text-bodygrey">Australians experiencing social isolation (%) — verified survey years only</p>
            <div className="mt-5 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={aihwData} margin={{ top: 20, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E3EEF2" vertical={false} />
                  <XAxis dataKey="year" type="category" tick={{ fontSize: 12, fill: "#061B44" }} axisLine={{ stroke: "#E3EEF2" }} tickLine={false} />
                  <YAxis domain={[0, 25]} tick={{ fontSize: 11, fill: "#5A6B85" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [`${v}%`, name]} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#061B44" }} iconType="plainline" />
                  <Line type="linear" dataKey="young" name="Aged 15–24" stroke="#18B8B4" strokeWidth={3} dot={{ r: 5, fill: "#18B8B4" }} activeDot={{ r: 7 }} />
                  <Line type="linear" dataKey="all" name="All Australians 15+" stroke="#061B44" strokeWidth={3} strokeDasharray="6 4" dot={{ r: 5, fill: "#061B44" }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <SourceLine label="Australian Institute of Health and Welfare — Social isolation and loneliness, using HILDA Survey data (waves 1–24)" url={AIHW_URL} linkText="View data →" testid="aihw-chart-source" />
            <p className="mt-2 text-sm text-bodygrey">
              Social isolation increased substantially among younger Australians during the pandemic. Although rates
              have declined since their peak, they remained above pre-pandemic levels for people aged 15–24 in 2024.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Australian data cards */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="au-data-cards">
        {auCards.map((c, i) => (
          <Reveal key={c.body} delay={i * 0.05}>
            <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-soft" data-testid={`au-data-card-${i}`}>
              <p className="font-heading text-3xl font-bold tracking-tight text-teal">{c.value}</p>
              <p className="mt-2 text-sm text-navy">{c.body}</p>
              <SourceLine label="AIHW / HILDA Survey (2024)" url={AIHW_URL} linkText="View source →" testid={`au-data-card-source-${i}`} />
            </div>
          </Reveal>
        ))}
      </div>

      {/* Where Orrbbit fits */}
      <Reveal className="mt-12 max-w-3xl">
        <div data-testid="where-orrbbit-fits">
          <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">Where Orrbbit Fits</h3>
          <p className="mt-3 text-bodygrey">
            These statistics don't mean technology alone can solve loneliness or social isolation. But they do
            highlight something important: opportunities for meaningful human connection matter.
          </p>
          <p className="mt-3 text-bodygrey">
            Orrbbit is designed to help create more of those opportunities by helping people discover others nearby
            who have already signalled that they're open to connecting.
          </p>
        </div>
      </Reveal>
      <Reveal className="mt-8">
        <div className="rounded-3xl border border-teal/20 bg-teal/5 p-7 text-center md:p-8">
          <p className="mx-auto max-w-2xl font-heading text-lg font-semibold tracking-tight text-navy md:text-xl">
            Orrbbit creates the opportunity. People create the connection.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export default ConnectionData;

